import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import { defineMessages, useIntl } from "react-intl";

import { srOnly } from "@labxchange/design_tokens";

import { toSpokenText, SpokenTextLabels } from "./toSpokenText";

/* ---------------------------- Internationalization ---------------------------- */

export const messages = defineMessages({
  superscript: {
    id: "StatusAnnouncer.superscript",
    defaultMessage: " superscript ",
    description:
      "Word a screen reader speaks in place of superscript markup, so 'x<sup>2</sup>' is announced as 'x superscript 2' rather than 'x two'. Keep the surrounding spaces.",
  },
  subscript: {
    id: "StatusAnnouncer.subscript",
    defaultMessage: " subscript ",
    description:
      "Word a screen reader speaks in place of subscript markup, so 'A<sub>e</sub>' is announced as 'A subscript e'. Keep the surrounding spaces.",
  },
});

/* ---------------------------------- Types ---------------------------------- */

export interface StatusAnnouncer {
  /**
   * Announce one complete status message. An empty string clears both regions
   * without announcing anything.
   */
  announce: (message: string) => void;
  /** Clear both regions silently. */
  clear: () => void;
  /** Render this inside the component that owns the status. */
  regions: React.ReactElement;
}

/* ----------------------------------- Hook ----------------------------------- */

/**
 * A question-level screen-reader status channel: one message at a time,
 * announced exactly once, from a region that was already mounted.
 *
 * Two visually-hidden polite regions stay mounted and are written in ping-pong.
 * Each `announce` writes the whole message into whichever region is currently
 * empty and clears the other in the same commit. Because the target region was
 * already mounted and empty, the write is a genuine text *addition* the screen
 * reader announces every time — even when two consecutive messages are
 * byte-identical (the classic WCAG 4.1.3 identical-string no-op). Clearing the
 * other region is a removal, which the default `aria-relevant` ("additions
 * text") does not announce, so it is silent.
 *
 * Why this rather than a `role="alert"` on the visible banner: a banner is
 * *inserted* on submit, and an inserted alert is the case VoiceOver reliably
 * drops. Where the insertion also shares a React commit with a focus move, the
 * alert is flushed by the focus announcement. An always-mounted polite region
 * has neither problem — but the caller is still responsible for ordering: if the
 * same interaction moves focus, announce *after* the focus has moved, or the
 * focus announcement can discard the queued utterance.
 *
 * There is deliberately no auto-clear timer: a guessed duration can truncate a
 * long readout. Callers clear on the next interaction instead, so a stale
 * message never lingers as a browse-mode stop.
 *
 * @param testId Optional stable test id; the regions get `-1` / `-2` suffixes.
 */
export const useStatusAnnouncer = (testId?: string): StatusAnnouncer => {
  const [announcements, setAnnouncements] = useState<[string, string]>([
    "",
    "",
  ]);

  // Which region is written next is derived from the state itself — only one
  // region ever holds text, so the empty one is always the next target. A
  // separate ref tracking the same fact would be a second source of truth.
  const announce = useCallback((message: string) => {
    setAnnouncements(([first]) => {
      if (!message) return ["", ""];
      return first === "" ? [message, ""] : ["", message];
    });
  }, []);

  const clear = useCallback(() => setAnnouncements(["", ""]), []);

  const regions = (
    <>
      <div
        className={srOnly}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        data-lx-status-announcer="1"
        data-testid={testId ? `${testId}-1` : undefined}
      >
        {announcements[0]}
      </div>
      <div
        className={srOnly}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        data-lx-status-announcer="2"
        data-testid={testId ? `${testId}-2` : undefined}
      >
        {announcements[1]}
      </div>
    </>
  );

  return { announce, clear, regions };
};

/**
 * `toSpokenText` bound to the current locale's superscript/subscript words.
 * Use this rather than the bare helper anywhere the markup can be authored
 * content, so science notation is not flattened into ambiguous digits.
 */
export const useSpokenText = (): ((markup: string) => string) => {
  const { formatMessage } = useIntl();

  const labels: SpokenTextLabels = useMemo(
    () => ({
      superscript: formatMessage(messages.superscript),
      subscript: formatMessage(messages.subscript),
    }),
    [formatMessage]
  );

  return useCallback(
    (markup: string) => toSpokenText(markup, labels),
    [labels]
  );
};
